import {
  TextInput, Button,
} from '@mantine/core';
import { Page } from '@/components';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import Loading from '@/components/Loading';
import { useTranslation } from 'react-i18next';
import { getMutationResult } from '@/helpers/query';
import {
  useGetCurrentMortgageCycleMixDataMutation,
  useAddCurrentMortgageCycleMixDataMutation,
} from '@/services/api';
import { IconZoomExclamation } from '@tabler/icons-react';
import classes from './style.module.css';

const MortgageCycleMixes = () => {
  const { t } = useTranslation();
  const [getCurrentMixDataConfig] = useGetCurrentMortgageCycleMixDataMutation();
  const [addCurrentMixDataMutationConfig] = useAddCurrentMortgageCycleMixDataMutation();
  const [loading, { open, close }] = useDisclosure(false);
  const [isError, setIsError] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const form = useForm({
    initialValues: {
      current: [],
      matrix: {},
    },
    validateInputOnBlur: true,
    validate: {},
  });

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const {
        data: currentMixData,
        isError: isErroIncurrentMixData,
      } = await getMutationResult({}, getCurrentMixDataConfig);

      if (isErroIncurrentMixData) {
        setIsError(true);
        setDataLoading(false);
        return;
      }

      const { data: mixData } = currentMixData;

      const matrix = {};
      mixData.forEach((group) => {
        group.tranches.forEach((tranche) => {
          if (!matrix[group.id]) matrix[group.id] = {};
          matrix[group.id][tranche.trancheId] = tranche.percentageForMix || '';
        });
      });

      form.setFieldValue('current', mixData);
      form.setFieldValue('matrix', matrix);
    } catch (error) {
      setIsError(true);
    }
    setDataLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const validateMixMatrix = () => {
    let hasErrors = false;

    form.values.current.forEach((group) => {
      let total = 0;
      group.tranches.forEach((tranche) => {
        const raw = form.values.matrix?.[group.id]?.[tranche.trancheId] ?? '';
        const num = Number(raw);

        // eslint-disable-next-line no-restricted-globals
        if (raw !== '' && (isNaN(num) || num < 0 || num > 100)) {
          form.setFieldError(`matrix.${group.id}.${tranche.trancheId}`, 'יש להזין מספר חוקי בין 0 ל־100');
          hasErrors = true;
        } else {
          form.clearFieldError(`matrix.${group.id}.${tranche.trancheId}`);
          total += num;
        }
      });

      if (total !== 100) {
        notifications.show({
          title: 'שגיאה בסכום האחוזים',
          message: `הסכום הכולל בתמהיל "${group.name}" חייב להיות בדיוק 100%. כרגע הוא ${total}%.`,
          color: 'red',
        });
        hasErrors = true;
      }
    });

    return hasErrors;
  };

  const handleSubmit = async () => {
    if (form.validate().hasErrors) return;
    if (validateMixMatrix()) return;

    open();
    try {
      const customMixes = form.values.current.map((group) => ({
        trancheId: group.id,
        tranches: group.tranches.map((tranche) => ({
          trancheId: tranche.trancheId,
          percentageForMix: Number(form.values.matrix?.[group.id]?.[tranche.trancheId] ?? 0),
        })),
      }));

      await addCurrentMixDataMutationConfig(customMixes);

      notifications.show({
        title: 'המידע נשמר בהצלחה',
        message: 'המידע נשמר בהצלחה',
      });
    } catch (error) {
      notifications.show({
        title: 'המידע לא נשמר בהצלחה',
        message: 'המידע לא נשמר בהצלחה',
        color: 'red',
      });
    } finally {
      close();
    }
  };

  if (dataLoading) return <Loading />;
  if (isError) {
    return (
      <div className={classes.errorContainer}>
        <h2>שגיאה בטעינת הנתונים</h2>
        <IconZoomExclamation size={16} />
      </div>
    );
  }

  return (
    <>
      <Page.Head
        title={t('pages.settings.pages.mortgageCycleMixes.title')}
        subtitle={t('pages.settings.pages.mortgageCycleMixes.description')}
      />
      <div className={classes.container}>
        <div className={classes.mixesWrapper}>
          {form.values.current.map((group) => (
            <div key={group.id} className={classes.tranchSection}>
              <h5>{group.name}</h5>
              {group.tranches.map((tranche) => (
                <TextInput
                  key={tranche.trancheId}
                  label={tranche.name}
                  value={form.values.matrix?.[group.id]?.[tranche.trancheId] ?? ''}
                  onChange={(e) => {
                    const updated = {
                      ...form.values.matrix[group.id],
                      [tranche.trancheId]: e.currentTarget.value,
                    };
                    form.setFieldValue(`matrix.${group.id}`, updated);
                  }}
                  error={form.errors?.[`matrix.${group.id}.${tranche.trancheId}`]}
                />
              ))}
            </div>
          ))}
        </div>
        <div className={classes.buttonsWrapper}>
          <Button loading={loading} onClick={handleSubmit} className={classes.submitButton}>
            שמירה
          </Button>
        </div>
      </div>
    </>
  );
};

export default MortgageCycleMixes;
